import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    Paper,
    Stack,
    Typography,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Chip,
    LinearProgress,
    Avatar
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import GroupsIcon from "@mui/icons-material/Groups";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import projectSvg from "assets/folders/project.svg";

import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import { fetchTasks } from "store/features/company/projectManagement/task/taskActions";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { selectProjects } from "store/features/company/projectManagement/project/projectSelectors";
import { selectTasks } from "store/features/company/projectManagement/task/taskSelectors";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";
import usePermissions from "hooks/usePermissions";

import { getSecureLocalItem } from "utils/secureSessionStorage";
import {
    selectEmployeeWorkloads,
    selectEmployeeWorkloadsLoading
} from "store/features/company/employee/employeeSelectors";
import { fetchEmployeeWorkloads } from "store/features/company/employee/employeeActions";


const STATUS_COMPLETED = ["completed", "done", "closed", "resolved"];



const formatDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getWorkloadLabel = (totalTasks, overdueTasks) => {
    if (totalTasks === 0) return "Balanced";
    const ratio = overdueTasks / totalTasks;
    if (ratio >= 0.25) return "Heavy";
    if (ratio >= 0.1) return "Moderate";
    return "Balanced";
};

const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
        case "todo":
            return "To Do";
        case "in_progress":
            return "In Progress";
        case "review":
            return "Review";
        case "done":
            return "Done";
        case "paused":
            return "Paused";
        case "blocked":
            return "Blocked";
        default:
            return status || "N/A";
    }
};

const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
        case "todo":
            return { bg: "#fef3c7", color: "#92400e" };
        case "in_progress":
            return { bg: "#dbeafe", color: "#1e40af" };
        case "review":
            return { bg: "#f3e8ff", color: "#6b21a5" };
        case "done":
            return { bg: "#d1fae5", color: "#065f46" };
        case "paused":
            return { bg: "#fef3c7", color: "#92400e" };
        case "blocked":
            return { bg: "#fee2e2", color: "#991b1b" };
        default:
            return { bg: "#e5e7eb", color: "#374151" };
    }
};

const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
        case "urgent":
            return { bg: "#fee2e2", color: "#dc2626" };
        case "high":
            return { bg: "#ffedd5", color: "#f97316" };
        case "medium":
            return { bg: "#fef9c3", color: "#ca8a04" };
        case "low":
            return { bg: "#dcfce7", color: "#22c55e" };
        default:
            return { bg: "#e5e7eb", color: "#9ca3af" };
    }
};

const getPendingTimeColor = (pendingHours) => {
    if (pendingHours < 80) {
        return {
            main: "#22c55e",
            bg: "rgba(34, 197, 94, 0.12)",
            text: "#166534",
            label: "< 80 hrs",
        };
    }
    if (pendingHours <= 120) {
        return {
            main: "#f97316",
            bg: "rgba(249, 115, 22, 0.12)",
            text: "#9a3412",
            label: "80-120 hrs",
        };
    }
    return {
        main: "#ef4444",
        bg: "rgba(239, 68, 68, 0.12)",
        text: "#991b1b",
        label: "> 120 hrs",
    };
};

const Overview = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const projects = useSelector(selectProjects);
    const tasks = useSelector(selectTasks);
    const employees = useSelector(selectEmployees);
    const workloads = useSelector(selectEmployeeWorkloads);
    const workloadsLoading = useSelector(selectEmployeeWorkloadsLoading);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [activeFilter, setActiveFilter] = useState("Total");
    const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

    const userRaw = getSecureLocalItem("user");
    const user = userRaw ? JSON.parse(userRaw) : null;
    const userId = user?.id;

    // alert("Overview.jsx: userId: " + userId);

    useEffect(() => {
        dispatch(fetchProjects());
        dispatch(fetchTasks());
        dispatch(fetchEmployees());
        dispatch(fetchEmployeeWorkloads());
    }, [dispatch]);

    const allowedProjects = useMemo(() => {
        return projects.filter((project) => {
            if (hasPermission("overview.all_overview")) return true;
            if (
                hasPermission("overview.personal_overview") ||
                hasPermission("overview.personal_and_team_overview")
            ) {
                return project.team_members?.includes(Number(userId));
            }
            return false;
        });
    }, [projects, hasPermission, userId]);

    useEffect(() => {
        if (!selectedProjectId && allowedProjects.length > 0) {
            setSelectedProjectId(allowedProjects[0].id);
        }
    }, [allowedProjects, selectedProjectId]);

    const projectChartData = useMemo(() => {
        const totalHoursByProject = {};
        const pendingHoursByProject = {};

        tasks.forEach((task) => {
            const id = task.project_id;
            const hours = parseFloat(task.estimated_hours || task.actual_hours) || 0;
            const isCompleted = STATUS_COMPLETED.includes(task.status?.toLowerCase());

            if (!totalHoursByProject[id]) totalHoursByProject[id] = 0;
            if (!pendingHoursByProject[id]) pendingHoursByProject[id] = 0;

            totalHoursByProject[id] += hours;
            if (!isCompleted) {
                pendingHoursByProject[id] += hours;
            }
        });

        return allowedProjects
            .map((project) => {
                const totalHours = Math.round((totalHoursByProject[project.id] || 0) * 100) / 100;
                const pendingHours = Math.round((pendingHoursByProject[project.id] || 0) * 100) / 100;
                const colorInfo = getPendingTimeColor(pendingHours);

                return {
                    id: project.id,
                    name: project.name || project.code || `Project ${project.id}`,
                    hours: totalHours,
                    pendingHours: pendingHours,
                    colorInfo: colorInfo,
                    color: colorInfo.main,
                };
            });
    }, [allowedProjects, tasks]);

    const selectedProject = projects.find((project) => project.id === selectedProjectId);
    const selectedProjectTasks = tasks.filter((task) => task.project_id === selectedProjectId);

    const filteredTasksByPermission = useMemo(() => {
        let baseTasks = selectedProjectTasks;

        if (hasPermission("overview.all_overview")) {
            return baseTasks;
        } else if (hasPermission("overview.personal_and_team_overview")) {
            const teamMembers = selectedProject?.team_members || [];
            return baseTasks.filter((task) =>
                !task.assigned_to ||
                teamMembers.includes(Number(task.assigned_to)) ||
                Number(task.assigned_to) === Number(userId)
            );
        } else if (hasPermission("overview.personal_overview")) {
            return baseTasks.filter((task) => Number(task.assigned_to) === Number(userId));
        }

        return [];
    }, [selectedProjectTasks, selectedProject, hasPermission, userId]);

    const filteredTasksForTable = useMemo(() => {
        let baseTasks = filteredTasksByPermission;

        if (activeFilter === "Total") {
            return baseTasks;
        } else if (activeFilter === "Completed") {
            return baseTasks.filter((task) =>
                STATUS_COMPLETED.includes(task.status?.toLowerCase())
            );
        } else if (activeFilter === "Pending") {
            return baseTasks.filter((task) =>
                !STATUS_COMPLETED.includes(task.status?.toLowerCase())
            );
        } else if (activeFilter === "Employees") {
            // return baseTasks.filter((task) => task.assigned_to !== null && task.assigned_to !== undefined);
            return baseTasks;
        } else if (activeFilter === "Overdue") {
            return baseTasks.filter((task) => {
                const dueDate = task.due_date ? new Date(task.due_date) : null;
                return (
                    !STATUS_COMPLETED.includes(task.status?.toLowerCase()) &&
                    dueDate &&
                    dueDate < new Date()
                );
            });
        }
        return baseTasks;
    }, [filteredTasksByPermission, activeFilter]);
    const projectEmployees = employees.filter((employee) => {
        if (selectedProject?.team_members && Array.isArray(selectedProject.team_members)) {
            return selectedProject.team_members.includes(Number(employee.id));
        }
        return selectedProjectTasks.some(
            (task) => Number(task.assigned_to) === Number(employee.id)
        );
    });

    const totalTasks = filteredTasksByPermission.length;
    const completedTasks = filteredTasksByPermission.filter((task) =>
        STATUS_COMPLETED.includes(task.status?.toLowerCase()),
    ).length;
    const pendingTasks = totalTasks - completedTasks;
    const employeesCount = (selectedProject?.team_members && Array.isArray(selectedProject.team_members))
        ? selectedProject.team_members.length
        : new Set(
            filteredTasksByPermission
                .map((task) => task.assigned_to)
                .filter((id) => id !== null && id !== undefined),
        ).size;
    const overdueTasks = filteredTasksByPermission.filter((task) => {
        const dueDate = task.due_date ? new Date(task.due_date) : null;
        return (
            !STATUS_COMPLETED.includes(task.status?.toLowerCase()) &&
            dueDate &&
            dueDate < new Date()
        );
    }).length;
    const workload = getWorkloadLabel(totalTasks, overdueTasks);

    const chartData = projectChartData.length
        ? projectChartData
        : [{ id: 0, name: "No task hours", hours: 1 }];

    const colors = [
        theme.palette.primary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
    ];

    const handleSelectProject = (entry) => {
        if (entry && entry.id) {
            setSelectedProjectId(entry.id);
        }
    };

    const filteredWorkloads = useMemo(() => {
        let baseWorkloads = workloads || [];

        if (hasPermission("overview.all_overview")) {
            // Keep all
        } else if (hasPermission("overview.personal_and_team_overview")) {
            const myProjects = projects.filter(project => project.team_members?.includes(Number(userId)));
            const myTeamMemberIds = new Set();
            myProjects.forEach(project => {
                if (Array.isArray(project.team_members)) {
                    project.team_members.forEach(id => myTeamMemberIds.add(Number(id)));
                }
            });
            baseWorkloads = baseWorkloads.filter(w => myTeamMemberIds.has(Number(w.employee_id)));
        } else if (hasPermission("overview.personal_overview")) {
            baseWorkloads = baseWorkloads.filter(w => Number(w.employee_id) === Number(userId));
        } else {
            baseWorkloads = [];
        }

        if (!searchText.trim()) {
            return baseWorkloads;
        }

        const search = searchText.toLowerCase();

        return baseWorkloads.filter((employee) => {
            const name = employee.name?.toLowerCase() || "";
            const role = employee.role?.toLowerCase() || "";
            const employeeProjects =
                employee.projects?.join(" ").toLowerCase() || "";

            return (
                name.includes(search) ||
                role.includes(search) ||
                employeeProjects.includes(search)
            );
        });
    }, [workloads, searchText, hasPermission, userId, projects]);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 4, display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                <Box
                    component="img"
                    src={projectSvg}
                    alt="Projects"
                    sx={{
                        mr: 1,
                        width: { xs: 64, lg: 80 },
                        height: { xs: 64, lg: 80 },
                        objectFit: "contain",
                    }}
                />
                <Box>
                    <Typography
                        sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: "warning.main",
                            mb: 0.5,
                        }}
                    >
                        Project Management
                    </Typography>

                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            lineHeight: 1.1,
                            color: "text.primary",
                        }}
                    >
                        Overview
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            mt: 1,
                        }}
                    >
                        View project summaries, team workload and overall progress.
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={5}>
                {/* LEFT CARD */}
                <Grid
                    item
                    xs={12}
                    md={6}
                >
                    <Box
                        sx={{
                            p: 3,
                            height: 430,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Project Distribution
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                mb: 1,
                            }}
                        >
                            Task Hours per Project
                        </Typography>

                        {/* Pending Time Color Threshold Legend */}
                        {/* <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#22c55e" }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    &lt; 80 hrs (Green)
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#f97316" }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    80-120 hrs (Orange)
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#ef4444" }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    &gt; 120 hrs (Red)
                                </Typography>
                            </Box>
                        </Stack> */}

                        {/* Project Hours */}
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: "auto",
                                pr: 1,
                            }}
                        >
                            {chartData.map((item) => {
                                const maxHours = Math.max(
                                    ...chartData.map((p) => (p.pendingHours > 0 ? p.pendingHours : p.hours)),
                                    1
                                );

                                const percentage = Math.max(((item.pendingHours || item.hours) / maxHours) * 100, 5);

                                return (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            mb: 2.5,
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={0.7}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        bgcolor: item.colorInfo?.main || item.color,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {item.name}
                                                </Typography>
                                            </Box>

                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                                                <Chip
                                                    size="small"
                                                    label={`${item.pendingHours} hrs pending`}
                                                    sx={{
                                                        height: 20,
                                                        fontSize: "0.7rem",
                                                        fontWeight: 700,
                                                        bgcolor: "transparent",
                                                        color: item.colorInfo?.text || item.colorInfo?.main,
                                                        borderRadius: 1,
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ whiteSpace: "nowrap" }}
                                                >
                                                    ({item.hours} hrs total)
                                                </Typography>
                                            </Stack>
                                        </Stack>

                                        <Box
                                            sx={{
                                                width: "100%",
                                                height: 8,
                                                borderRadius: 5,
                                                bgcolor: "action.hover",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: `${Math.min(percentage, 100)}%`,
                                                    height: "100%",
                                                    bgcolor: item.colorInfo?.main || item.color,
                                                    borderRadius: 5,
                                                    transition: ".3s",
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Project Selector */}
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{
                                mt: 2,
                            }}
                        >
                            {chartData.map((item) => (
                                <Chip
                                    key={item.id}
                                    clickable
                                    label={`${item.name} (${item.pendingHours}h)`}
                                    onClick={() =>
                                        handleSelectProject(item)
                                    }
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        bgcolor:
                                            selectedProjectId === item.id
                                                ? item.colorInfo?.main || item.color
                                                : "action.hover",
                                        color:
                                            selectedProjectId === item.id
                                                ? "#fff"
                                                : "text.primary",
                                        "&:hover": {
                                            bgcolor: item.colorInfo?.main || item.color,
                                            color: "#fff",
                                        },
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>
                </Grid>

                {/* RIGHT CARD */}
                <Grid
                    item
                    xs={12}
                    sx={{
                        flexBasis: { md: "57%" },
                        maxWidth: { md: "60%" },
                    }}
                >
                    <Box
                        sx={{
                            p: 3,
                            height: 430,
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: 'transparent'
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{

                                fontWeight: 600,
                                mb: 0.5,
                            }}
                        >
                            {selectedProject
                                ? `${selectedProject.name} Tasks`
                                : "Project Details"}
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3 }}
                        >
                            Click a project slice to view summary
                            details.
                        </Typography>

                        <Grid container spacing={3}>
                            {[
                                {
                                    label: "Total",
                                    value: totalTasks,
                                    icon: <AssignmentIcon fontSize="small" />,
                                },
                                {
                                    label: "Completed",
                                    value: completedTasks,
                                    icon: <CheckCircleIcon fontSize="small" color="success" />,
                                },
                                {
                                    label: "Pending",
                                    value: pendingTasks,
                                    icon: <PendingActionsIcon fontSize="small" color="warning" />,
                                },
                                {
                                    label: "Overdue",
                                    value: overdueTasks,
                                    icon: <WarningAmberIcon fontSize="small" color="error" />,
                                },
                            ].map((item) => (
                                <Grid
                                    item
                                    xs={6}
                                    sm={3}
                                    key={item.label}
                                >
                                    <Box
                                        onClick={() => setActiveFilter(item.label)}
                                        sx={{
                                            px: 4,
                                            py: 1,
                                            borderRadius: 1,
                                            border: "1px solid",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderColor: activeFilter === item.label ? "primary.main" : "divider",
                                            height: 88,
                                            display: "flex",
                                            flexDirection: "column",
                                            cursor: "pointer",
                                            backgroundColor: activeFilter === item.label
                                                ? (theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.06)')
                                                : 'transparent',
                                            boxShadow: activeFilter === item.label ? "0px 4px 10px rgba(0, 0, 0, 0.05)" : "none",
                                            transition: "all 0.25s ease",
                                            "&:hover": {
                                                borderColor: "primary.main",
                                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                                                transform: "translateY(-2px)",
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                mb: 1,
                                            }}
                                        >
                                            {item.icon}

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {item.label}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                // mt: 0.5,
                                                my: 0,
                                                py: 0
                                            }}
                                        >
                                            {item.value}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        <Grid container spacing={5}>
                            {[
                                {
                                    label: "Due Date",
                                    value: formatDate(
                                        selectedProject?.end_date
                                    ),
                                },
                                {
                                    label: "Workload",
                                    value: workload,
                                },
                                {
                                    label: "Employees",
                                    value: employeesCount,
                                },
                            ].map((item) => (
                                <Grid
                                    item
                                    xs={6}
                                    sm={4}
                                    key={item.label}
                                >
                                    <Box
                                        sx={{

                                            px: 0,
                                            py: 2,
                                            borderRadius: 2,
                                            borderColor:
                                                "divider",
                                            height: 88,
                                            display: "flex",
                                            flexDirection:
                                                "column",
                                            justifyContent:
                                                "center",
                                            backgroundColor: 'transparent'
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            textTransform={"uppercase"}
                                        >
                                            {item.label}
                                        </Typography>

                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                mt: 0.5,
                                            }}
                                        >
                                            {item.value}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        <Box sx={{ flexGrow: 1 }} />

                        <Box
                            sx={{
                                mt: 3,
                                px: 0,
                                py: 2,
                                borderRadius: 2,
                                // border: "1px solid",
                                borderColor: "divider",
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontWeight: 300, alignItems: 'left', textAlign: 'left', letterSpacing: '2px', textTransform: 'uppercase' }}
                            >
                                Selected Project
                            </Typography>

                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, alignItems: 'left', textAlign: 'left' }}
                            >
                                {selectedProject?.name || "N/A"}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Tasks Table Section */}
            {activeFilter && (
                <Box sx={{ mt: 5, mb: 5 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={3}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.14em",
                                    textTransform: "uppercase",
                                    color: "warning.main",
                                    mb: 0.5,
                                }}
                            >
                                Project Tasks
                            </Typography>

                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: "text.primary",
                                    lineHeight: 1.15,
                                }}
                            >
                                {activeFilter} Tasks
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    mt: 0.75,
                                }}
                            >
                                Viewing tasks filter: {activeFilter} for project {selectedProject?.name || "N/A"}
                            </Typography>
                        </Box>
                    </Stack>

                    <Paper
                        sx={{
                            borderRadius: 1,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                            background: "transparent",
                        }}
                    >
                        {filteredTasksForTable.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: "center" }}>
                                <Typography color="text.secondary">
                                    No tasks found matching filter "{activeFilter}".
                                </Typography>
                            </Box>
                        ) : (
                            <DataGrid
                                autoHeight
                                rows={filteredTasksForTable.map((task) => ({
                                    id: task.id,
                                    ...task,
                                }))}
                                columns={[
                                    {
                                        field: "title",
                                        headerName: "Task Title",
                                        flex: 1.5,
                                        minWidth: 200,
                                        renderCell: (params) => (
                                            <Typography fontWeight={600} variant="body2">
                                                {params.value}
                                            </Typography>
                                        )
                                    }, {
                                        field: "description",
                                        headerName: "Task Description",
                                        flex: 1.5,
                                        minWidth: 200,
                                        renderCell: (params) => (
                                            <Typography fontWeight={600} variant="body2">
                                                {params.value}
                                            </Typography>
                                        )
                                    },
                                    {
                                        field: "assigned_to",
                                        headerName: "Assigned To",
                                        flex: 1.2,
                                        minWidth: 180,
                                        renderCell: (params) => {
                                            const employeeId = params.value;
                                            const employee = employees.find((e) => Number(e.id) === Number(employeeId));
                                            if (!employee) return <Typography variant="body2" sx={{ color: "text.secondary" }}>Unassigned</Typography>;

                                            return (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Avatar
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            fontSize: "0.75rem",
                                                            bgcolor: "primary.main",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {`${employee.first_name?.[0] ?? ""}${employee.last_name?.[0] ?? ""}`}
                                                    </Avatar>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {employee.first_name} {employee.last_name}
                                                    </Typography>
                                                </Stack>
                                            );
                                        }
                                    },
                                    {
                                        field: "priority",
                                        headerName: "Priority",
                                        width: 120,
                                        renderCell: (params) => {
                                            const colors = getPriorityColor(params.value);
                                            return (
                                                <Chip
                                                    size="small"
                                                    label={params.value?.toUpperCase() || "MEDIUM"}
                                                    sx={{
                                                        bgcolor: colors.bg,
                                                        color: colors.color,
                                                        fontWeight: 600,
                                                        borderRadius: 1,
                                                    }}
                                                />
                                            );
                                        }
                                    },
                                    {
                                        field: "status",
                                        headerName: "Status",
                                        width: 140,
                                        renderCell: (params) => {
                                            const colors = getStatusColor(params.value);
                                            return (
                                                <Chip
                                                    size="small"
                                                    label={getStatusLabel(params.value)}
                                                    sx={{
                                                        bgcolor: colors.bg,
                                                        color: colors.color,
                                                        fontWeight: 600,
                                                        borderRadius: 1,
                                                    }}
                                                />
                                            );
                                        }
                                    },
                                    {
                                        field: "due_date",
                                        headerName: "Due Date",
                                        width: 150,
                                        renderCell: (params) => (
                                            <Typography variant="body2">
                                                {formatDate(params.value)}
                                            </Typography>
                                        ),
                                    },
                                ]}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 5,
                                            page: 0,
                                        },
                                    },
                                }}
                                pageSizeOptions={[5, 10, 25]}
                                disableRowSelectionOnClick
                                sx={{
                                    border: 0,
                                    "& .MuiDataGrid-columnHeaders": {
                                        backgroundColor: "action.hover",
                                        fontWeight: 700,
                                    },
                                    "& .MuiDataGrid-cell": {
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        display: "flex",
                                        alignItems: "center",
                                    },
                                    "& .MuiDataGrid-footerContainer": {
                                        borderTop: "1px solid",
                                        borderColor: "divider",
                                    },
                                }}
                            />
                        )}
                    </Paper>
                </Box>
            )}

            <Grid container spacing={2}>
                {hasAnyPermission([
                    "overview.personal_and_team_overview",
                    "overview.all_overview"
                ]) &&
                    projectEmployees.slice(0, 6).map((employee) => {
                        const workload =
                            workloads?.find(
                                (w) => Number(w.employee_id) === Number(employee.id)
                            ) || {};

                        const percentage = Number(workload.workload_percentage || 0);

                        return (
                            <Grid item xs={12} md={6} key={employee.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        transition: ".25s",
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        <Avatar
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                bgcolor: "primary.main",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {`${employee.first_name?.[0] ?? ""}${employee.last_name?.[0] ?? ""}`}
                                        </Avatar>

                                        <Box flex={1}>
                                            <Typography fontWeight={700}>
                                                {employee.first_name} {employee.last_name}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {employee.designation_name || employee.role}
                                            </Typography>

                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(percentage, 100)}
                                                sx={{
                                                    mt: 1,
                                                    mb: 0.5,
                                                    height: 7,
                                                    borderRadius: 5,
                                                }}
                                            />

                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                mt={1}
                                            >
                                                <Typography variant="caption" sx={{
                                                    py: 0.2,
                                                    px: 1,
                                                    bgcolor: "#ffffff38",
                                                    borderRadius: 2,
                                                    mr: 1
                                                }}>
                                                    {workload.active_tasks || 0} Tasks
                                                </Typography>

                                                <Typography variant="caption" sx={{
                                                    py: 0.2,
                                                    px: 1,
                                                    bgcolor: "#ffffff38",
                                                    borderRadius: 2,
                                                    mr: 1
                                                }}>
                                                    {workload.remaining_hours || 0} hrs
                                                </Typography>

                                                <Typography variant="caption" sx={{
                                                    py: 0.2,
                                                    px: 1,
                                                    bgcolor: "#ffffff38",
                                                    borderRadius: 2,
                                                }}>
                                                    {percentage}%
                                                </Typography>
                                            </Stack>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: "block",
                                                    mt: 1,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {workload.projects?.join(" • ") ||
                                                    "No Project Assigned"}
                                            </Typography>
                                        </Box>

                                        <Chip
                                            size="small"
                                            label={workload.workload_status?.toUpperCase()}
                                            color={
                                                workload.workload_status === "high"
                                                    ? "error"
                                                    : workload.workload_status === "medium"
                                                        ? "warning"
                                                        : "success"
                                            }
                                        />
                                    </Stack>
                                </Paper>
                            </Grid>
                        );
                    })}

            </Grid>


            {hasAnyPermission([
                "overview.personal_overview",
                "overview.personal_and_team_overview",
                "overview.all_overview"
            ]) && (
                    <Box sx={{ mt: 6 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            mb={3}
                        >
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: "0.72rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.14em",
                                        textTransform: "uppercase",
                                        color: "warning.main",
                                        mb: 0.5,
                                    }}
                                >
                                    Resource Management
                                </Typography>

                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 700,
                                        color: "text.primary",
                                        lineHeight: 1.15,
                                    }}
                                >
                                    Employee Workload
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "text.secondary",
                                        mt: 0.75,
                                        mb: 1,
                                    }}
                                >
                                    Monitor employee capacity, active assignments and workload distribution.
                                </Typography>
                            </Box>

                            <Box sx={{ flexGrow: 1 }} />

                            <TextField
                                size="small"
                                placeholder="Search employee..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                sx={{
                                    width: 320,
                                }}
                            />
                        </Stack>

                        <Paper
                            sx={{
                                borderRadius: 1,
                                overflow: "hidden",
                                border: "1px solid",
                                borderColor: "divider",
                                background: "transparent",
                            }}
                        >
                            <DataGrid
                                autoHeight
                                rows={filteredWorkloads.map((employee) => ({
                                    id: employee.employee_id,
                                    ...employee,
                                }))}
                                columns={[
                                    {
                                        field: "name",
                                        headerName: "Employee",
                                        flex: 1.4,
                                        minWidth: 220,
                                    },
                                    {
                                        field: "role",
                                        headerName: "Role",
                                        flex: 1,
                                        minWidth: 170,
                                    },
                                    {
                                        field: "active_projects",
                                        headerName: "Projects",
                                        type: "number",
                                        width: 110,
                                    },
                                    {
                                        field: "active_tasks",
                                        headerName: "Tasks",
                                        type: "number",
                                        width: 100,
                                    },
                                    {
                                        field: "remaining_hours",
                                        headerName: "Remaining Hours",
                                        width: 160,
                                        renderCell: (params) => (
                                            <Typography fontWeight={600}>
                                                {params.value} hrs
                                            </Typography>
                                        ),
                                    },
                                    {
                                        field: "workload_percentage",
                                        headerName: "Workload",
                                        width: 150,
                                        renderCell: (params) => (
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        height: 8,
                                                        bgcolor: "action.hover",
                                                        borderRadius: 5,
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: `${Math.min(
                                                                params.value,
                                                                100
                                                            )}%`,
                                                            height: "100%",
                                                            bgcolor:
                                                                params.value >= 80
                                                                    ? "error.main"
                                                                    : params.value >= 50
                                                                        ? "warning.main"
                                                                        : "success.main",
                                                        }}
                                                    />
                                                </Box>

                                                <Typography
                                                    variant="caption"
                                                    fontWeight={600}
                                                >
                                                    {params.value}%
                                                </Typography>
                                            </Box>
                                        ),
                                    },
                                    {
                                        field: "workload_status",
                                        headerName: "Status",
                                        width: 120,
                                        renderCell: (params) => (
                                            <Chip
                                                size="small"
                                                label={params.value?.toUpperCase()}
                                                color={
                                                    params.value === "high"
                                                        ? "error"
                                                        : params.value === "medium"
                                                            ? "warning"
                                                            : "success"
                                                }
                                            />
                                        ),
                                    },
                                ]}
                                initialState={{
                                    sorting: {
                                        sortModel: [
                                            {
                                                field: "workload_percentage",
                                                sort: "desc",
                                            },
                                        ],
                                    },
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10,
                                            page: 0,
                                        },
                                    },
                                }}
                                pageSizeOptions={[10, 25, 50, 100]}
                                disableRowSelectionOnClick
                                onRowClick={(params) => {
                                    console.log(params.row);
                                    // Later:
                                    // openEmployeeDrawer(params.row)
                                }}
                                sx={{
                                    border: 0,

                                    "& .MuiDataGrid-columnHeaders": {
                                        backgroundColor: "action.hover",
                                        fontWeight: 700,
                                    },

                                    "& .MuiDataGrid-row:hover": {
                                        backgroundColor: "action.hover",
                                        cursor: "pointer",
                                    },

                                    "& .MuiDataGrid-cell": {
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        display: "flex",
                                        alignItems: "center",
                                    },

                                    "& .MuiDataGrid-footerContainer": {
                                        borderTop: "1px solid",
                                        borderColor: "divider",
                                    },
                                }}
                            />
                        </Paper>
                    </Box>
                )}
        </Box>
    );
};

export default Overview;
